/**
 * Les quarts d'heure sécurité, adossés à Supabase.
 *
 * Écrire passe par `noter_quart_heure_securite`, pour le terrain comme pour le
 * bureau : c'est elle qui vérifie le chantier, la date et l'animateur. Lire
 * passe par la table, que la base borne aux chantiers où l'appelant a affaire.
 * L'équipe d'un chantier vient de `site_crew`, le terrain ne lisant pas les
 * affectations des autres.
 */
import { DomainError } from "@dker/core";
import type {
  CrewMember,
  ExecutionContext,
  RecordSafetyTalkInput,
  SafetyRepository,
  SafetyTalk,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

/** Les refus écrits pour l'utilisateur (`P0001`) passent tels quels. */
function fail(error: { message: string; code?: string }): never {
  const lisible = error.code === "P0001";
  throw new DomainError(
    lisible ? "invalid_input" : "conflict",
    lisible ? error.message : `Opération refusée par la base : ${error.message}`,
    { pgCode: error.code },
  );
}

const one = (raw: unknown): Row | null =>
  raw == null ? null : ((Array.isArray(raw) ? raw[0] : raw) as Row | null) ?? null;

function toTalk(row: Row): SafetyTalk {
  const animateur = one(row.employees);
  return {
    id: row.id,
    projectId: row.project_id,
    projectName: one(row.projects)?.name ?? null,
    heldOn: row.held_on,
    topic: row.topic,
    ledByEmployeeId: row.led_by_employee_id ?? null,
    ledByName: animateur ? `${animateur.first_name} ${animateur.last_name}`.trim() : null,
    notes: row.notes ?? null,
    attendeeIds: ((row.safety_talk_attendees ?? []) as Row[]).map((a) => a.employee_id),
  };
}

// `projects(name)` est nul pour un compte de terrain, qui ne lit pas `projects` :
// l'écran du chantier connaît déjà son nom.
const COLONNES = `
  id, project_id, held_on, topic, led_by_employee_id, notes,
  projects(name),
  employees!safety_talks_led_by_employee_id_fkey(first_name, last_name),
  safety_talk_attendees(employee_id)
`;

export function createSupabaseSafetyRepository(clientFor: DbClientFactory): SafetyRepository {
  return {
    async list(ctx: ExecutionContext, filter: { projectIds?: string[]; from?: string }) {
      let q = clientFor(ctx)
        .from("safety_talks")
        .select(COLONNES)
        .order("held_on", { ascending: false })
        .order("created_at", { ascending: false });
      if (filter.projectIds) q = q.in("project_id", filter.projectIds);
      if (filter.from) q = q.gte("held_on", filter.from);
      const { data, error } = await q;
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toTalk);
    },

    async record(ctx: ExecutionContext, command: Omit<RecordSafetyTalkInput, "confirmationId">) {
      const { data, error } = await clientFor(ctx).rpc("noter_quart_heure_securite", {
        p_project_id: command.projectId,
        p_topic: command.topic,
        p_attendees: command.attendeeIds,
        p_notes: command.notes ?? null,
        p_held_on: command.heldOn ?? null,
        p_led_by: command.ledByEmployeeId ?? null,
      });
      if (error) fail(error);
      return data as string;
    },

    async remove(ctx: ExecutionContext, id: string) {
      const { error } = await clientFor(ctx).from("safety_talks").delete().eq("id", id);
      if (error) fail(error);
    },

    async crew(ctx: ExecutionContext, projectId: string, on?: string): Promise<CrewMember[]> {
      const { data, error } = await clientFor(ctx).rpc("site_crew", {
        p_project_id: projectId,
        p_on: on ?? null,
      });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((r) => ({
        employeeId: r.employee_id,
        name: r.employee_name,
        roleOnSite: r.role_on_site ?? null,
      }));
    },
  };
}
